import { useEffect, useMemo, useState } from 'react'
import { agents } from '../../../../../../api/agents'
import { readStoredThread } from './threadStorage'

/**
 * Resolve which thread to restore for (agentId, viewScope) on mount.
 *
 * Strategy: restore only when localStorage AND the server agree on a non-ended
 * threadId. localStorage is our only view-scoping signal today — the server
 * query is scoped only by (firm, user, agentId). If we used the server as a
 * fallback when localStorage is empty, opening a different view in a new tab
 * would load the most recent thread from whichever view the user touched last.
 *
 *  1. Stored threadId for this (agentId, viewScope) in localStorage.
 *  2. Server's latest non-ended thread for (firm, user, agentId).
 *  3. Resolve to stored iff stored === server. Otherwise no restore.
 *
 * Consequence: cross-device / cleared-storage users never auto-restore. This is
 * an acceptable tradeoff until the server learns view-scoping via a context key.
 *
 * Returns { threadId, isReady } — isReady is false while the server query is loading.
 *
 * @param {string} agentId
 * @param {string|number} viewScope — e.g. viewId, or 'new' for builder
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]
 */
export function useRestoredThreadId (agentId, viewScope, { enabled = true } = {}) {
  const { data, isLoading, isFetched } = agents.useLatestAgentThread(agentId, { enabled })

  const storedThreadId = useMemo(
    () => readStoredThread(agentId, viewScope),
    [agentId, viewScope]
  )

  const [resolved, setResolved] = useState(null)

  useEffect(() => {
    if (!enabled) {
      setResolved(null)
      return
    }
    if (!isFetched) return

    const serverThreadId = data?.threadId || null

    if (storedThreadId && storedThreadId === serverThreadId) {
      setResolved(storedThreadId)
      return
    }

    setResolved(null)
  }, [enabled, isFetched, data?.threadId, storedThreadId])

  return {
    threadId: resolved,
    isReady: !enabled || (isFetched && !isLoading)
  }
}
